import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SwPush } from '@angular/service-worker';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PushService {
  /** Whether THIS browser is currently registered as subscribed with our
   *  server. Cleared by reset() on logout / user change so the next login
   *  re-runs subscribe and re-upserts the endpoint against the new
   *  member_id. Without this, a phone shared at the station keeps pushing
   *  to whichever user subscribed first. */
  private subscribed = false;

  /** In-flight subscribe promise. Two concurrent callers (e.g. AppComponent
   *  reacting to currentUser$ at the same instant a banner tap fires)
   *  must NOT both call pushManager.subscribe() — that throws
   *  InvalidStateError and leaves the device in a half-subscribed state.
   *  Late callers await the in-flight one and return when it finishes. */
  private inFlight: Promise<void> | null = null;

  /**
   * True once the user has dismissed the in-app "Enable alerts" banner this
   * session. The banner can re-appear on the next app load — push is too
   * important to permanently silence.
   */
  promptDismissed = false;

  constructor(
    private http: HttpClient,
    private swPush: SwPush
  ) {
    this.swPush.notificationClicks.subscribe(({ action, notification }) => {
      console.log('[Push] Notification clicked:', action, notification);
    });

    this.swPush.messages.subscribe((msg) => {
      console.log('[Push] Message received in-app:', msg);
    });
  }

  /** Clear the "subscribed" flag so the next subscribeToPush() actually runs.
   *  Called by AppComponent whenever the logged-in user changes (logout,
   *  or login as a different user on a shared device). We do NOT clear the
   *  browser-level pushManager subscription — it's tied to the device, not
   *  the user, and the next subscribeToPush() will re-upsert it against
   *  the new member_id via /push/subscribe. */
  reset(): void {
    this.subscribed = false;
  }

  /**
   * Subscribe the browser to web push notifications.
   *
   * @param userInitiated  Pass true when called from an actual click/tap
   *   handler. iOS Safari (16.4+) and recent Chromium versions only allow
   *   Notification.requestPermission() inside a user gesture; outside one,
   *   the call silently fails (or, worse, locks the user into a soft-denied
   *   state). When userInitiated is false we therefore only proceed if
   *   permission is already 'granted' (i.e. refreshing an existing
   *   subscription after a VAPID rotation or sign-out/sign-in).
   */
  async subscribeToPush(userInitiated = false): Promise<void> {
    // Coalesce concurrent calls so we never run two pushManager.subscribe()
    // races in parallel.
    if (this.inFlight) { await this.inFlight; return; }
    if (this.subscribed) return;

    this.inFlight = this.doSubscribe(userInitiated)
      .finally(() => { this.inFlight = null; });
    await this.inFlight;
  }

  private async doSubscribe(userInitiated: boolean): Promise<void> {
    if (!this.swPush.isEnabled) {
      console.log('[Push] SwPush not enabled (dev mode or no SW)');
      return;
    }

    const perm = this.permissionState;
    console.log('[Push] subscribeToPush start', { userInitiated, permission: perm });

    if (perm === 'unsupported') {
      console.log('[Push] Notification API unavailable on this browser');
      return;
    }
    if (perm === 'denied') {
      console.log('[Push] Permission denied — user must re-enable in browser/OS settings');
      return;
    }
    if (perm === 'default' && !userInitiated) {
      console.log('[Push] Skipping auto-subscribe; waiting for user gesture');
      return;
    }

    try {
      // Ask for permission FIRST, before ANY other await. iOS Safari only
      // honors Notification.requestPermission() while the tap's transient
      // user-activation is alive; the VAPID-key fetch that used to run
      // before this call consumed the activation, so on iPhones the prompt
      // silently no-oped and the device never subscribed (Android is
      // lenient about activation surviving a short await, which is why it
      // worked there). This must stay the first await in the gesture path.
      if (this.permissionState === 'default') {
        const granted = await Notification.requestPermission();
        console.log('[Push] Notification.requestPermission ->', granted);
        if (granted !== 'granted') return;
      }

      const resp = await this.http.get<{ publicKey: string }>(`${environment.apiUrl}/push/vapid-key`).toPromise();
      if (!resp?.publicKey) {
        console.error('[Push] VAPID key not configured on server');
        return;
      }
      console.log('[Push] VAPID key fetched:', resp.publicKey.slice(0, 20) + '...');

      // After a server-side VAPID rotation, the browser still holds the
      // previous subscription locally. pushManager.subscribe() throws
      // InvalidStateError if you try to subscribe with a different
      // applicationServerKey than the existing subscription. Detect the
      // mismatch and unsubscribe the stale one first.
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const existing = reg ? await reg.pushManager.getSubscription() : null;
        if (existing) {
          const existingKey = existing.options?.applicationServerKey as ArrayBuffer | null | undefined;
          if (existingKey && this.arrayBufferToBase64(existingKey) !== resp.publicKey) {
            console.log('[Push] VAPID key changed, unsubscribing stale subscription');
            await existing.unsubscribe();
          }
        }
      } catch (e) {
        console.warn('[Push] stale-subscription check failed (continuing):', e);
      }

      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: resp.publicKey
      });

      console.log('[Push] Subscribed:', subscription.endpoint.slice(0, 60) + '...');

      const key = subscription.getKey('p256dh');
      const auth = subscription.getKey('auth');
      if (!key || !auth) {
        console.error('[Push] Missing p256dh or auth keys');
        return;
      }

      const body = {
        endpoint: subscription.endpoint,
        p256dh: this.arrayBufferToBase64(key),
        auth: this.arrayBufferToBase64(auth)
      };

      // HttpClient throws on non-2xx so reaching the next line means the
      // server stored (or re-pointed) the subscription successfully.
      await this.http.post(`${environment.apiUrl}/push/subscribe`, body).toPromise();
      this.subscribed = true;
      console.log('[Push] Subscription saved to server');
    } catch (err) {
      console.error('[Push] Subscribe error:', err);
    }
  }

  /**
   * Current Notification permission, or 'unsupported' when the API is
   * unavailable (e.g. iOS Safari outside an installed PWA).
   */
  get permissionState(): NotificationPermission | 'unsupported' {
    if (typeof Notification === 'undefined') return 'unsupported'
    return Notification.permission
  }

  /**
   * True when we can still request permission AND we haven't subscribed yet.
   * Drives the "Enable dispatch alerts" banner.
   */
  get needsUserPrompt(): boolean {
    return this.swPush.isEnabled
      && this.permissionState === 'default'
      && !this.subscribed
      && !this.promptDismissed
  }

  /**
   * True when permission was previously denied. The browser will not show
   * its prompt again — the user has to flip the switch in OS / browser
   * settings. The banner switches to a "Notifications blocked" message.
   */
  get permissionBlocked(): boolean {
    return this.swPush.isEnabled
      && this.permissionState === 'denied'
      && !this.subscribed
      && !this.promptDismissed
  }

  dismissPrompt(): void {
    this.promptDismissed = true
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
